import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  PaginationState,
} from "@tanstack/react-table";
import { InfoIcon } from "../../../../assets/svgs/InfoIcon";
import { ReportDataModel } from "../../../../sharedTypes/report-types";
import ReactPaginate from "react-paginate";
import { EmptyStateNoFiles } from "../../../../components/Common";
import { format, parseISO } from "date-fns";
import { PAGE_SIZE } from "../../../../constants";
import { getStatusStyles } from "../../../../utils";
import { StatusType } from "../../../../utils/models";

interface reportsProps {
  data: ReportDataModel[];
  total: number;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  pagination: PaginationState;
}

const DateCell = ({ value }: { value: string }) => {
  if (!value) return <p>N/A</p>;

  try {
    const date = parseISO(value);
    return <p>{format(date, "dd/MM/yyyy")}</p>;
  } catch (error) {
    return <p>Invalid Date</p>;
  }
};

export const ReportTable: React.FC<reportsProps> = (props) => {
  const { setPagination, pagination, data, total } = props;

  const columnHelper = createColumnHelper<ReportDataModel>();

  const columns = [
    columnHelper.accessor("id", {
      header: () => <p className="tw-whitespace-nowrap tw-text-left">Id</p>,
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor("username", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Username</p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor("initiated_on", {
      header: () => <p className="tw-whitespace-nowrap tw-text-left">Date</p>,
      cell: (info) => <DateCell value={info.getValue()} />,
    }),

    columnHelper.accessor("request", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Request</p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">
            {info.getValue()?.substring(0, 30)}...
          </p>
          <span className="tw-w-fit tw-max-w-[300px] tw-h-fit  tw-absolute tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md tw-whitespace-pre-line">
            <p className="tw-max-w-full tw-whitespace-pre-line tw-break-words">
              {info.getValue()}
            </p>
          </span>
        </div>
      ),
    }),

    columnHelper.accessor("response", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Response</p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">
            {info.getValue()?.substring(0, 30)}...
          </p>
          <span className="tw-w-fit tw-max-w-[300px] tw-max-h-[150px] tw-overflow-y-scroll  tw-absolute tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md">
            <p className="tw-max-w-full tw-whitespace-pre-line tw-break-words">
              {info.getValue()}
            </p>
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("clientId", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Client ID</p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">
            {info.getValue()?.toString()?.substring(0, 30)}...
          </p>
          <span className="tw-w-fit tw-max-w-[300px] tw-max-h-[150px] tw-overflow-y-scroll  tw-absolute tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md">
            <p className="tw-max-w-full tw-whitespace-pre-line tw-break-words">
              {info.getValue()}
            </p>
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("callType", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">API Service</p>
      ),
      cell: (info) => (
        <p className="tw-whitespace-nowrap tw-text-left">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("reference", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Reference ID</p>
      ),
      cell: (info) => (
        <p className="tw-whitespace-nowrap tw-text-left">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("fee", {
      header: () => (
        <span className="tw-group tw-relative tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
          <p className="tw-whitespace-nowrap tw-text-left">Fee</p>
          <InfoIcon className="tw-w-4 tw-h-4" />
          <span className="tw-w-fit tw-max-w-[300px]  tw-absolute tw-top-[1.5em] tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md tw-whitespace-nowrap">
            More info on fee
          </span>
        </span>
      ),
      cell: (info) => <p>₦ {info.renderValue()}</p>,
    }),
    columnHelper.accessor("amount", {
      header: () => <p className="tw-whitespace-nowrap tw-text-left">Amount</p>,
      cell: (info) => <p>₦ {info.getValue()}</p>,
    }),
    columnHelper.accessor("status", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Response Status</p>
      ),
      cell: (info) => (
        <p
          className={`tw-px-[1em] tw-text-[.875em] tw-text-left tw-w-fit tw-py-[.4em] tw-border-[.5px] tw-rounded-full ${getStatusStyles(
            info?.getValue() as StatusType
          )}`}
        >
          {info.getValue()}
        </p>
      ),
    }),
  ];

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const startItem = (pagination.pageIndex - 1) * pagination.pageSize + 1;
  const endItem = Math.min(
    pagination.pageIndex * pagination.pageSize,
    total as number
  );

  const handlePageClick = (event: { selected: number }) => {
    setPagination({ ...pagination, pageIndex: event.selected + 1 });
  };

  // console.log(data)

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-overflow-x-auto">
      {data?.length > 0 ? (
        <>
          <table className="tw-min-w-full tw-h[20em] tw-table-auto tw-bg-white">
            <thead className="tw-bg-neutral-200">
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="tw-py-[1em] tw-px-[1em] tw-text-[.875em] tw-text-neutral-700"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableInstance.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="tw-text-neutral-800 !tw-text-[.75em]  tw-font-[300] tw-px-[1em] tw-py-[2em]"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="tw-w-full tw-flex tw-justify-end tw-mt-[2em]">
            <div className="tw-w-fit tw-flex tw-items-center tw-gap-[1em]">
              <p className="tw-text-[.75em] tw-font-[600] tw-text-neutral-900">
                {startItem} - {endItem} of {total}
              </p>
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                initialPage={pagination.pageIndex - 1}
                pageCount={Math.ceil(total / PAGE_SIZE)}
                previousLabel="<"
                renderOnZeroPageCount={null}
                containerClassName={"pagination"}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="tw-mx-auto">
          <EmptyStateNoFiles text="No transactions to report yet" />
        </div>
      )}
    </div>
  );
};
